import { MAX_REQUEST_BYTES, type AchievementClaimResponse, type AdClaimResponse, type AdStatusResponse, type HintPurchaseResponse, type PurchaseResponse, type RecoveryIssueResponse, type SyncOutcome } from "@nano/contracts";
import type { HouseId } from "@nano/game-config";
import { defaultGameConfig, packagedGameConfig, resolveGameConfig } from "@nano/game-config";
import { getSave, putOutbox, refreshOutbox, removeOutbox, replaceSave, updateSave, backupLocal } from "../../app/persist.ts";
import { appStore, setUi } from "../../app/store.ts";
import type { ApiClient } from "../api/client.ts";
import { ApiClientError } from "../api/client.ts";
import { applySnapshot, emptySave, levelKey, saveFromSnapshot } from "../save/progress.ts";
import type { OutboxEntry, SaveData, Stars } from "../save/types.ts";
import { backoffDelay } from "./backoff.ts";

const STATUS_TTL_MS = 10 * 60_000;
const MAX_REQUESTS_PER_FLUSH = 5;
/** Keep each sync body well under the Worker's size limit (long Color Lab proofs are big). */
const SYNC_BODY_BUDGET = Math.floor(MAX_REQUEST_BYTES * 0.8);
const network = defaultGameConfig.network;

function uuid(): string {
  return crypto.randomUUID();
}

/**
 * Talks to the Worker only when there is something to say: guest creation,
 * batched proof sync, purchases, equipment, profile, leaderboard on demand.
 * Every failure falls back to local play; nothing here blocks gameplay.
 */
export class SyncService {
  private failures = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private kickTimer: ReturnType<typeof setTimeout> | null = null;
  private flushing: Promise<void> | null = null;
  private statusFetchedAt = 0;
  private pendingPurchaseKeys = new Map<string, string>();
  private pendingHintKey: { readonly slot: string; readonly key: string } | null = null;

  constructor(
    private readonly api: ApiClient,
    private readonly now: () => number = () => Date.now(),
  ) {}

  start(): void {
    if (typeof window === "undefined") return;
    window.addEventListener("online", () => {
      setUi({ online: true });
      this.failures = 0;
      this.kick("online", 0);
    });
    window.addEventListener("offline", () => setUi({ online: false, sync: "offline" }));
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") void this.flush();
    });
    void this.refreshStatus();
    this.kick("start", 300);
  }

  private get cloudEnabled(): boolean {
    return appStore.get().ui.config.flags.cloudSyncEnabled;
  }

  private canTalk(): boolean {
    const { online } = appStore.get().ui;
    return online && this.cloudEnabled && !getSave().syncMeta.sessionLost;
  }

  /** Debounced "something changed, sync soon". */
  kick(_reason: string, delayMs = 800): void {
    if (this.kickTimer) clearTimeout(this.kickTimer);
    this.kickTimer = setTimeout(() => void this.flush(), delayMs);
  }

  /** Flush only if the outbox is worth a request (threshold or age). */
  maybeFlush(): void {
    const pending = appStore.get().outbox.filter((entry) => entry.status === "pending");
    if (pending.length === 0) return;
    const oldest = Math.min(...pending.map((entry) => entry.createdAt));
    if (pending.length >= network.syncFlushThreshold || this.now() - oldest > 60_000) this.kick("threshold", 200);
  }

  async refreshStatus(force = false): Promise<void> {
    if (!appStore.get().ui.online) return;
    if (!force && this.now() - this.statusFetchedAt < STATUS_TTL_MS) return;
    try {
      const status = await this.api.status();
      this.statusFetchedAt = this.now();
      // Remote config can only switch things off (resolveGameConfig enforces it).
      setUi({ config: resolveGameConfig(packagedGameConfig, status.config) });
      if (!status.config.cloudSyncEnabled) setUi({ sync: "disabled" });
    } catch {
      // Status is advisory; keep packaged config.
    }
  }

  private scheduleRetry(error: unknown): void {
    if (error instanceof ApiClientError && !error.transient) return;
    this.failures += 1;
    const wait = Math.max(backoffDelay(this.failures), ((error as ApiClientError)?.retryAfterSec ?? 0) * 1000);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => void this.flush(), wait);
    setUi({ sync: appStore.get().ui.online ? "error" : "offline" });
  }

  private handleAuthLoss(error: unknown): boolean {
    if (error instanceof ApiClientError && error.code === "UNAUTHENTICATED") {
      void updateSave((save) => ({ ...save, session: null, syncMeta: { ...save.syncMeta, sessionLost: true } }));
      setUi({ sync: "session_lost" });
      return true;
    }
    return false;
  }

  async ensureSession(): Promise<boolean> {
    const save = getSave();
    if (save.session && save.session.expiresAt > this.now()) return true;
    if (!this.canTalk() || !save.profile.avatarId) return false;
    const auth = await this.api.createGuest({
      localGuestId: save.profile.guestId,
      avatarId: save.profile.avatarId,
      ...(save.profile.displayName ? { displayName: save.profile.displayName } : {}),
      deviceLabel: "web",
    });
    await updateSave((current) => applySnapshot({ ...current, session: auth.session, profile: { ...current.profile, profileDirty: false } }, auth.snapshot));
    // A brand-new server profile gets the local equipment pushed next.
    if (Object.keys(getSave().equipped).length > 0) await updateSave((current) => ({ ...current, equipmentDirty: true }));
    return true;
  }

  /** Runs one bounded sync pass. Concurrent callers share the same pass. */
  flush(): Promise<void> {
    this.flushing ??= this.runFlush().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private async runFlush(): Promise<void> {
    if (!appStore.get().save) return;
    if (!this.cloudEnabled) {
      setUi({ sync: "disabled" });
      return;
    }
    if (!appStore.get().ui.online) {
      setUi({ sync: "offline" });
      return;
    }
    if (getSave().syncMeta.sessionLost) {
      setUi({ sync: "session_lost" });
      return;
    }
    setUi({ sync: "syncing" });
    try {
      if (!(await this.ensureSession())) {
        setUi({ sync: "idle" });
        return;
      }
      await this.pushProfile();
      await this.pushEquipment();
      for (let request = 0; request < MAX_REQUESTS_PER_FLUSH; request += 1) {
        const sent = await this.pushOutboxBatch();
        if (!sent) break;
      }
      this.failures = 0;
      await updateSave((save) => ({ ...save, syncMeta: { ...save.syncMeta, lastSyncedAt: this.now() } }));
      setUi({ sync: "idle" });
    } catch (error) {
      if (this.handleAuthLoss(error)) return;
      this.scheduleRetry(error);
    }
  }

  private async pushOutboxBatch(): Promise<boolean> {
    const outbox = await refreshOutbox();
    const batch: OutboxEntry[] = [];
    let bytes = 200;
    for (const entry of outbox) {
      if (entry.status !== "pending") continue;
      if (batch.length >= network.maxResultsPerSync) break;
      const size = JSON.stringify(entry.proof).length + 120;
      if (batch.length > 0 && bytes + size > SYNC_BODY_BUDGET) break;
      batch.push(entry);
      bytes += size;
    }
    if (batch.length === 0) return false;
    const response = await this.api.sync({
      clientMutationId: uuid(),
      baseRevision: getSave().server?.wallet.revision ?? 0,
      results: batch.map((entry) => ({
        houseId: entry.houseId,
        levelId: entry.levelId,
        contentVersion: entry.contentVersion,
        proof: entry.proof,
      })),
    });

    const done: number[] = [];
    const rejected: OutboxEntry[] = [];
    const outcomeById = new Map<number, SyncOutcome>();
    const verified: Record<string, Stars> = { ...(getSave().server?.verified ?? {}) };
    response.results.forEach((outcome, index) => {
      const entry = batch[index];
      if (!entry) return;
      outcomeById.set(entry.id, outcome);
      if (outcome.status === "rejected") {
        rejected.push({ ...entry, status: "rejected", ...(outcome.reason ? { reason: outcome.reason } : {}) });
        return;
      }
      done.push(entry.id);
      if (outcome.bestStars) verified[levelKey(entry.houseId, entry.levelId)] = outcome.bestStars;
    });

    await updateSave((save) =>
      save.server
        ? { ...save, server: { ...save.server, wallet: response.wallet, verified } }
        : save,
    );
    // Only confirmed events leave the outbox; rejected ones stay as evidence.
    await removeOutbox(done);
    await putOutbox(rejected);
    this.markResult(outcomeById);
    return outbox.filter((entry) => entry.status === "pending").length > batch.length;
  }

  /** Updates the open result screen once its proof is confirmed or rejected. */
  private markResult(outcomes: ReadonlyMap<number, SyncOutcome>): void {
    const result = appStore.get().ui.result;
    if (!result || result.outboxId === null) return;
    const outcome = outcomes.get(result.outboxId);
    if (!outcome) return;
    if (outcome.status === "rejected") {
      setUi({ result: { ...result, status: "rejected", ...(outcome.reason ? { reason: outcome.reason } : {}) } });
    } else {
      setUi({ result: { ...result, status: "confirmed", confirmedGold: outcome.goldAwarded } });
    }
  }

  private async pushEquipment(): Promise<void> {
    const save = getSave();
    if (!save.equipmentDirty || !save.session) return;
    try {
      const response = await this.api.equip({ equipped: save.equipped });
      await updateSave((current) => ({ ...current, equipped: response.equipped, equipmentDirty: false }));
    } catch (error) {
      if (error instanceof ApiClientError && !error.transient && error.code !== "UNAUTHENTICATED") {
        // Server disagrees (e.g. item not owned there): take the server copy.
        const snapshot = await this.api.me();
        await updateSave((current) => applySnapshot({ ...current, equipmentDirty: false }, snapshot));
        return;
      }
      throw error;
    }
  }

  private async pushProfile(): Promise<void> {
    const save = getSave();
    if (!save.profile.profileDirty || !save.session || !save.profile.avatarId) return;
    try {
      const response = await this.api.patchProfile({
        avatarId: save.profile.avatarId,
        ...(save.profile.displayName ? { displayName: save.profile.displayName } : {}),
      });
      await updateSave((current) => ({
        ...current,
        profile: { ...current.profile, profileDirty: false, displayName: response.displayName },
        equipped: current.equipmentDirty ? current.equipped : response.equipped,
      }));
    } catch (error) {
      if (error instanceof ApiClientError && error.code === "PROFILE_COOLDOWN") return;
      if (error instanceof ApiClientError && error.code === "INVALID_INPUT") {
        await updateSave((current) => ({ ...current, profile: { ...current.profile, profileDirty: false } }));
        return;
      }
      throw error;
    }
  }

  /** Purchases need the server; the idempotency key survives retries of the same tap. */
  async purchase(itemId: string): Promise<PurchaseResponse> {
    if (!appStore.get().ui.online) throw new ApiClientError("NETWORK", 0);
    if (!(await this.ensureSession())) throw new ApiClientError("FEATURE_DISABLED", 503);
    await this.flush();
    const key = this.pendingPurchaseKeys.get(itemId) ?? uuid();
    this.pendingPurchaseKeys.set(itemId, key);
    const response = await this.api.purchase({ itemId, idempotencyKey: key, equip: true });
    this.pendingPurchaseKeys.delete(itemId);
    await updateSave((save) => ({
      ...save,
      equipped: response.equipped,
      equipmentDirty: false,
      server: save.server ? { ...save.server, wallet: response.wallet, owned: response.owned } : save.server,
    }));
    return response;
  }

  /**
   * Buys one extra hint for the level being played. The server charges
   * the configured price; `slot` names this exact purchase (house, level,
   * attempt, how many bought so far) so a retried tap reuses its
   * idempotency key and is never charged twice.
   */
  async buyHint(houseId: HouseId, levelId: number, slot: string): Promise<HintPurchaseResponse> {
    if (!appStore.get().ui.online) throw new ApiClientError("NETWORK", 0);
    if (!(await this.ensureSession())) throw new ApiClientError("FEATURE_DISABLED", 503);
    await this.flush();
    const key = this.pendingHintKey?.slot === slot ? this.pendingHintKey.key : uuid();
    this.pendingHintKey = { slot, key };
    const response = await this.api.buyHint({ houseId, levelId, idempotencyKey: key });
    this.pendingHintKey = null;
    await updateSave((save) => (save.server ? { ...save, server: { ...save.server, wallet: response.wallet } } : save));
    return response;
  }

  /**
   * Asks the server to pay an achievement. Pending results are flushed
   * first so the server sees the same progress the player sees.
   */
  async claimAchievement(achievementId: string): Promise<AchievementClaimResponse> {
    if (!appStore.get().ui.online) throw new ApiClientError("NETWORK", 0);
    if (!(await this.ensureSession())) throw new ApiClientError("FEATURE_DISABLED", 503);
    await this.flush();
    const response = await this.api.claimAchievement(achievementId);
    await updateSave((save) =>
      save.server ? { ...save, server: { ...save.server, wallet: response.wallet, claimedAchievements: response.claimed } } : save,
    );
    return response;
  }

  async leaderboard(force = false): Promise<{ stale: boolean }> {
    const cached = getSave().leaderboard;
    const fresh = cached && this.now() - cached.fetchedAt < network.leaderboardCacheSeconds * 1000;
    if (fresh && !force) return { stale: false };
    if (!appStore.get().ui.online || !appStore.get().ui.config.flags.leaderboardEnabled) return { stale: true };
    try {
      await this.flush();
      const [top, me] = await Promise.all([
        this.api.leaderboard(network.leaderboardTopLimit),
        getSave().session ? this.api.leaderboardMe().catch(() => null) : Promise.resolve(null),
      ]);
      await updateSave((save) => ({ ...save, leaderboard: { top, me, fetchedAt: this.now() } }));
      return { stale: false };
    } catch {
      return { stale: true };
    }
  }

  async issueRecovery(): Promise<RecoveryIssueResponse> {
    if (!(await this.ensureSession())) throw new ApiClientError("NETWORK", 0);
    await this.flush();
    const pending = appStore.get().outbox.filter((entry) => entry.status === "pending").length;
    if (pending > 0) throw new ApiClientError("SYNC_CONFLICT", 409);
    const issued = await this.api.issueRecovery();
    await updateSave((save) => (save.server ? { ...save, server: { ...save.server, hasRecoveryCode: true } } : save));
    return issued;
  }

  /** The server id a rewarded ad names in Google's signed callback (a session is made if needed). */
  async adIdentity(): Promise<string | null> {
    if (!appStore.get().ui.online) return null;
    if (!(await this.ensureSession().catch(() => false))) return null;
    return getSave().server?.playerId ?? null;
  }

  async adStatus(): Promise<AdStatusResponse> {
    if (!appStore.get().ui.online) throw new ApiClientError("NETWORK", 0);
    if (!(await this.ensureSession())) throw new ApiClientError("FEATURE_DISABLED", 503);
    return this.api.adStatus();
  }

  /**
   * Asks the server to pay a watched rewarded ad. Pending results go first,
   * so a level's doubled gold finds the clear it rides on.
   */
  async claimAd(reward: string): Promise<AdClaimResponse> {
    if (!appStore.get().ui.online) throw new ApiClientError("NETWORK", 0);
    if (!(await this.ensureSession())) throw new ApiClientError("FEATURE_DISABLED", 503);
    await this.flush();
    const response = await this.api.claimAd(reward);
    await updateSave((save) => (save.server ? { ...save, server: { ...save.server, wallet: response.wallet } } : save));
    return response;
  }

  /**
   * Data deletion: erases the server profile behind this device's session,
   * then the device starts over with a fresh guest. A device that never
   * synced has nothing on the server to erase.
   */
  async deleteEverything(): Promise<void> {
    if (getSave().session) {
      if (!appStore.get().ui.online) throw new ApiClientError("NETWORK", 0);
      await this.api.deleteMe();
    }
    await removeOutbox(appStore.get().outbox.map((entry) => entry.id));
    await replaceSave(emptySave(crypto.randomUUID(), this.now()));
  }

  /** Replaces the local journey with the one behind the recovery code. */
  async restore(code: string): Promise<SaveData> {
    const auth = await this.api.restore({ code, deviceLabel: "web" });
    await backupLocal();
    const next = saveFromSnapshot(getSave(), auth.snapshot, auth.session);
    await replaceSave(next);
    setUi({ sync: "idle" });
    return next;
  }
}
